import React, { useEffect, useState, useMemo } from 'react';
import { Accordion, RadioButtons, Typography } from '@guildeducationinc/recess';
import { useHits } from 'react-instantsearch-hooks-web';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import {
	useSearchContext,
	useSearchDispatch,
} from '@src/components/Providers/SearchProvider/SearchProvider';
import { useGetMemberProgramStartDatesV2Query } from '@src/graphqlTypes/catalogServiceTypes';
import { categorizeProgramsByStartDate } from '@src/utilities/startDateFilterUtility';
import { trackFilterEvents } from '../filterHelpers';

export const StartDateFilter: React.FC = () => {
	const [toggleAccordion, setToggleAccordion] = useState<boolean>(false);
	const [selectedStartDate, setSelectedStartDate] = useState<string>('any_start_date');

	const { results } = useHits();
	const { userFromIdToken: user } = useMemberExperienceAuth();
	const { index, memberUuid, employerUuid, startDateFilter } = useSearchContext();
	const { dispatch } = useSearchDispatch();

	const { data: programsData, loading: programsLoading } = useGetMemberProgramStartDatesV2Query({
		variables: {
			memberUuid: memberUuid || '',
			employerUuid: employerUuid || '',
			version: 'v2',
		},
		skip: !memberUuid || !employerUuid,
	});

	const programsByStartDate = useMemo(() => {
		if (!programsData?.memberPrograms) {
			return {
				next_30_days: [],
				next_60_days: [],
				next_90_days: [],
			};
		}
		return categorizeProgramsByStartDate(programsData.memberPrograms);
	}, [programsData?.memberPrograms]);

	useEffect(() => {
		setSelectedStartDate(startDateFilter || 'any_start_date');
	}, [startDateFilter]);

	const FilterTitle: React.FC = () => {
		return (
			<Typography testid='start-date-title' variant='h6'>
				{`Start date ${selectedStartDate !== 'any_start_date' ? '(1)' : ''}`}
			</Typography>
		);
	};

	const handleStartDateChange = (value: string) => {
		setSelectedStartDate(value);
		setToggleAccordion(true);

		// For "any_start_date", clear the filter by setting empty array
		// For other options, use the categorized program UUIDs
		const programUuids = value === 'any_start_date' ? [] : programsByStartDate[value] || [];

		dispatch({
			type: 'set_start_date_filter',
			startDateFilter: value,
			startDateProgramUuids: programUuids,
		});

		const eventName = 'FilterAdded';
		trackFilterEvents('startDate', eventName, 'startDate', value, index, results, user);
	};

	// Check if the currently selected option has no programs and reset to "any_start_date" if needed
	useEffect(() => {
		if (
			selectedStartDate !== 'any_start_date' &&
			programsByStartDate[selectedStartDate]?.length === 0
		) {
			setSelectedStartDate('any_start_date');
			dispatch({
				type: 'set_start_date_filter',
				startDateFilter: 'any_start_date',
				startDateProgramUuids: [],
			});
		}
	}, [programsByStartDate, selectedStartDate, dispatch]);

	if (
		programsLoading ||
		!programsData?.memberPrograms ||
		programsData?.memberPrograms.length === 0
	) {
		return null;
	}

	return (
		<Accordion defaultOpen={toggleAccordion} testid='startDateFilterAccordion' title={FilterTitle}>
			<RadioButtons
				groupLabel='Start date'
				hideGroupLabel
				name='Start date'
				onChange={e => {
					handleStartDateChange(e.target.value);
				}}
				selectedValue={selectedStartDate}
				values={[
					{
						label: 'Any start date',
						value: 'any_start_date',
					},
					{
						label: 'Next 30 days',
						value: 'next_30_days',
						disabled: programsByStartDate.next_30_days.length === 0,
					},
					{
						label: 'Next 60 days',
						value: 'next_60_days',
						disabled: programsByStartDate.next_60_days.length === 0,
					},
					{
						label: 'Next 90 days',
						value: 'next_90_days',
						disabled: programsByStartDate.next_90_days.length === 0,
					},
				]}
			/>
		</Accordion>
	);
};
